type FlushablePromise<Value> = Promise<Value> & { flush: () => Promise<Value> };

type FlushableFunction<ArgsType extends unknown[], Value> = {
  (...args: ArgsType): FlushablePromise<Value>;
  flush: () => void;
};

function throttleable<ArgsType extends unknown[], Value>(
  callback: (...args: ArgsType) => Promise<Value>,
  delay: 0,
  { type }: { type: "async" },
): FlushableFunction<ArgsType, Value>;

function throttleable<ArgsType extends unknown[], Value>(
  callback: (...args: ArgsType) => Promise<Value> | Value,
  delay: number,
  {
    leading,
    trailing,
    type,
  }: { leading?: boolean; trailing?: boolean; type: "debounce" | "throttle" },
): FlushableFunction<ArgsType, Value>;

/*
  Throttles calls in various ways, returns a promise that resolves whenever the delay has passed
 */
function throttleable<ArgsType extends unknown[], Value>(
  callback: (...args: ArgsType) => Promise<Value> | Value,
  delay: number,
  {
    leading = false,
    trailing = true,
    type,
  }: {
    leading?: boolean;
    trailing?: boolean;
    type: "async" | "debounce" | "throttle";
  },
): FlushableFunction<ArgsType, Value> {
  const nextCall = {
    delayTimer: undefined as ReturnType<typeof setTimeout> | undefined,
    args: null as ArgsType | null,
    promise: null as FlushablePromise<Value> | null,
    resolve: null as ((value: Promise<Value> | Value) => void) | null,
    reject: null as ((error: unknown) => void) | null,
  };

  function clearNextCall() {
    nextCall.promise = null;
    nextCall.resolve = null;
    nextCall.reject = null;
  }

  function callCallback(args: ArgsType) {
    const result = callback(...args);
    nextCall.args = null;
    return Promise.resolve(result);
  }

  const resolveNextCall = () => {
    const resolve = nextCall.resolve;
    const reject = nextCall.reject;
    clearNextCall();
    if (!nextCall.args || !resolve || !reject || !trailing) {
      return;
    }

    try {
      resolve(callCallback(nextCall.args));
    } catch (error) {
      reject(error);
    }
  };

  function flush() {
    clearTimeout(nextCall.delayTimer);
    resolveNextCall();
  }

  function toFlushablePromise(
    promise: Promise<Value>,
  ): FlushablePromise<Value> {
    (promise as FlushablePromise<Value>).flush = () => {
      flush();
      return promise;
    };
    return promise as FlushablePromise<Value>;
  }

  const wrappedCallback = (...args: ArgsType) => {
    nextCall.args = args;

    if (type === "debounce") {
      clearTimeout(nextCall.delayTimer);
      nextCall.delayTimer = setTimeout(() => resolveNextCall(), delay);
    }

    // As long as a nextCall is queued return it. It will use the latest args when it gets called
    // When it is called it will clear the promise so that a new call can be queued below, restarting the chain
    if (nextCall.promise) {
      return nextCall.promise;
    }

    if (type === "throttle") {
      nextCall.delayTimer = setTimeout(() => resolveNextCall(), delay);
    }

    nextCall.promise = toFlushablePromise(
      new Promise((resolve, reject) => {
        nextCall.resolve = resolve;
        nextCall.reject = reject;
      }),
    );

    if (leading || type === "async") {
      const result = toFlushablePromise(callCallback(nextCall.args));
      if (type === "async") {
        void result.then(resolveNextCall);
      }
      return result;
    }

    return nextCall.promise;
  };

  wrappedCallback.flush = flush;

  return wrappedCallback;
}

/**
 * Debounces function calls to execute only after delay has passed since last call.
 *
 * @param callback - Function to debounce
 * @param delay - Wait time after last call (ms)
 * @param options.leading - Execute on leading edge (default: false)
 * @example const debouncedSearch = debounce(search, 300);
 */
function debounce<ArgsType extends unknown[], Value>(
  callback: (...args: ArgsType) => Promise<Value> | Value,
  delay: number,
  options: { leading?: boolean; trailing?: boolean } = {},
) {
  return throttleable(callback, delay, { ...options, type: "debounce" });
}

/**
 * Throttles function calls to execute at most once per delay period.
 *
 * @param callback - Function to throttle
 * @param delay - Minimum time between executions (ms)
 * @param options.leading - Execute on leading edge (default: false)
 * @example const throttledScroll = throttle(handleScroll, 100);
 */
function throttle<ArgsType extends unknown[], Value>(
  callback: (...args: ArgsType) => Promise<Value> | Value,
  delay: number,
  options: { leading?: boolean; trailing?: boolean } = {},
) {
  return throttleable(callback, delay, { ...options, type: "throttle" });
}

/**
 * Queues async function calls to prevent concurrent execution.
 * Each call waits for the previous promise to resolve.
 *
 * @param callback - Async function to queue
 * @example const queuedFetch = asyncThrottle(fetchData);
 */
function asyncThrottle<ArgsType extends unknown[], Value>(
  callback: (...args: ArgsType) => Promise<Value>,
) {
  return throttleable(callback, 0, { type: "async" });
}

export { asyncThrottle, debounce, throttle };
